// src/pages/StaffDependentsView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';
import TopHeader from '../../components/dashboard/TopHeader/TopHeader';

// Theme colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_LIGHT = '#fef3e2';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

const getAuthHeaders = () => ({
  'AuthorizationKey': process.env.REACT_APP_AUTH_KEY || '',
  'Token': process.env.REACT_APP_API_TOKEN || '',
  'SecretKey': process.env.REACT_APP_SECRET_KEY || '',
  'Content-Type': 'application/json',
});

const getUserSession = () => {
  try { return JSON.parse(localStorage.getItem('userSession') || '{}'); }
  catch { return {}; }
};

// Navigation items matching your sidebar structure
const getNavItems = (currentPath) => [
  {
    section: 'MAIN',
    items: [
      { name: 'Get Started', icon: 'fa-home', path: '/get-started', active: currentPath === '/get-started' }
    ]
  },
  {
    section: 'DASHBOARD',
    items: [
      { name: 'Analytics', icon: 'fa-chart-bar', path: '/dashboard', active: currentPath === '/dashboard' },
    ]
  },
  {
    section: 'Plan',
    items: [
      { name: 'Plan', icon: 'fa-box', path: '/products', active: currentPath === '/products' || currentPath?.includes('/staff') },
      { name: 'Hmo Links', icon: 'fa-link', path: '/insurance-links', active: currentPath === '/insurance-links' }
    ]
  },
  {
    section: 'Agent',
    items: [
      { name: 'Agents', icon: 'fa-user-tie', active: false, page: 'agents', path: '/agents' }
    ]
  },
  {
    section: 'Feedback',
    items: [
      { name: 'Feedback', icon: 'fa-comments', active: currentPath === '/feedback', path: '/feedback' }
    ]
  }
];

const StaffDependentsView = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffInfo, setStaffInfo] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Initialize AOS
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  // Fetch dependents when component mounts or staffId changes
  useEffect(() => {
    if (staffId) {
      fetchDependents();
      fetchStaffInfo();
    }
  }, [staffId]);

  const fetchStaffInfo = async () => {
    try {
      const session = getUserSession();
      // Fetch staff details from your existing endpoint
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/get-staff-by-product`,
        'POST',
        { productId: 1, companyId: session.companyId }, // Adjust productId as needed
        { headers: getAuthHeaders() }
      );
      
      if (response?.success && response?.data?.staff) {
        const staff = response.data.staff.find(s => s.staffId === parseInt(staffId));
        if (staff) {
          setStaffInfo(staff);
        }
      }
    } catch (err) {
      console.warn('Could not fetch staff info:', err);
    }
  };

  const fetchDependents = async () => {
    try {
      setLoading(true);
      const session = getUserSession();
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/get-dependents-by-staff`,
        'POST',
        { 
          StaffId: parseInt(staffId), 
          CompanyId: session.companyId 
        },
        { headers: getAuthHeaders() }
      );

      if (response?.success === true || response?.status === 'success') {
        setDependents(response.data || []);
      } else {
        toast.error(response?.message || 'Failed to load dependents');
        setDependents([]);
      }
    } catch (err) {
      console.error('Error fetching dependents:', err);
      setError(err.message || 'Failed to load dependents');
      toast.error('Failed to load dependents');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDependent = async (dependentId) => {
    if (!window.confirm('Are you sure you want to remove this dependent?')) return;

    try {
      const session = getUserSession();
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/delete-dependent`,
        'POST',
        { DependentId: dependentId, CompanyId: session.companyId },
        { headers: getAuthHeaders() }
      );

      if (response?.success === true) {
        toast.success('Dependent removed successfully');
        setDependents(prev => prev.filter(d => d.id !== dependentId));
      } else {
        toast.error(response?.message || 'Failed to remove dependent');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to remove dependent');
    }
  };

  const handleLogout = () => {
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const handleNavClick = (path, e) => {
    e.preventDefault();
    if (path) navigate(path);
  };

  const RelationshipBadge = ({ relationship }) => {
    const colors = {
      spouse: 'bg-primary',
      son: 'bg-success',
      daughter: 'bg-info',
      father: 'bg-warning',
      mother: 'bg-danger',
      sibling: 'bg-secondary',
      other: 'bg-dark'
    };
    
    return (
      <span className={`badge ${colors[relationship?.toLowerCase()] || 'bg-secondary'}`}>
        {relationship || 'N/A'}
      </span>
    );
  };

  const navItems = getNavItems(location.pathname);

  if (loading && !dependents.length) {
    return (
      <div className="product-details-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ color: BRAND_ORANGE, width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading dependents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ NORMAL SIDEBAR */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="brand-logo">
            <img src="../../images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link
                      to={item.path || '#'}
                      className={`nav-link ${item.active ? 'active' : ''}`}
                      onClick={(e) => item.path && handleNavClick(item.path, e)}
                    >
                      <i className={`fas ${item.icon} me-2`}></i>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <button className="nav-link" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </button>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <TopHeader
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="Manage Dependents"
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        
        <div className="content-wrapper">
          <div className="page-header" data-aos="fade-up">
            <div className="d-flex align-items-center gap-3 mb-3">
              <button className="btn-back" onClick={() => navigate(-1)}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <h2 className="mb-0">
                <i className="fas fa-users me-2" style={{ color: BRAND_ORANGE }}></i>
                Dependents
              </h2>
            </div>
            <small className="text-muted">
              {dependents.length} dependent{dependents.length !== 1 ? 's' : ''} for {staffInfo?.firstName || 'Staff Member'}
            </small>
          </div>

          {/* Staff Info Card */}
          <div className="card mb-4 border-0 shadow-sm" data-aos="fade-up">
            <div className="card-body d-flex align-items-center gap-3">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                style={{ 
                  width: '60px', 
                  height: '60px', 
                  backgroundColor: BRAND_ORANGE,
                  fontSize: '24px' 
                }}
              >
                {staffInfo?.firstName?.[0] || staffInfo?.lastName?.[0] || 'S'}
              </div>
              <div>
                
              </div>
              <div className="ms-auto">
                {dependents.length < 3 && (
                  <button 
                    className="btn text-white"
                    style={{ backgroundColor: BRAND_ORANGE }}
                    onClick={() => {
                      setEditingDependent(null);
                      setShowAddModal(true);
                    }}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Add Dependent
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Dependents Table */}
          <div className="card border-0 shadow-sm" data-aos="fade-up">
            <div className="card-body p-0">
              {dependents.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-user-friends fa-3x text-muted mb-3 opacity-25"></i>
                  <h5 className="text-muted">No Dependents Added</h5>
                  <p className="text-muted small mb-3">This staff member has no registered dependents yet.</p>
                  {dependents.length < 3 && (
                    <button 
                      className="btn text-white"
                      style={{ backgroundColor: BRAND_ORANGE }}
                      onClick={() => {
                        setEditingDependent(null);
                        setShowAddModal(true);
                      }}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add First Dependent
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Dependent</th>
                        <th>Relationship</th>
                        <th>Age</th>
                        <th>Gender</th>
                        <th>Contact</th>
                        <th>Date of Birth</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dependents.map((dep, index) => (
                        <tr key={dep.id || index} data-aos="fade-in" data-aos-delay={index * 50}>
                          <td className="fw-bold text-muted">{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {dep.photo ? (
                                <img 
                                  src={dep.photo} 
                                  alt={`${dep.firstName} ${dep.lastName}`}
                                  className="rounded-circle"
                                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                />
                              ) : (
                                <div 
                                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                  style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    backgroundColor: BRAND_ORANGE_LIGHT,
                                    color: BRAND_ORANGE,
                                    fontSize: '14px'
                                  }}
                                >
                                  {dep.firstName?.[0]}{dep.lastName?.[0]}
                                </div>
                              )}
                              <div>
                                <div className="fw-semibold">{dep.firstName} {dep.lastName}</div>
                                {dep.email && <small className="text-muted">{dep.email}</small>}
                              </div>
                            </div>
                          </td>
                          <td><RelationshipBadge relationship={dep.relationship} /></td>
                          <td>{dep.age || '-'}</td>
                          <td>
                            <span className={`badge ${dep.gender === 'male' ? 'bg-primary' : dep.gender === 'female' ? 'bg-pink' : 'bg-secondary'}`}>
                              {dep.gender || '-'}
                            </span>
                          </td>
                          <td>
                            {dep.phone && <small className="d-block">{dep.phone}</small>}
                            {dep.email && <small className="text-muted">{dep.email}</small>}
                          </td>
                          <td>
                            {dep.dateOfBirth 
                              ? new Date(dep.dateOfBirth).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '-'
                            }
                          </td>
                          <td className="text-end">
                            <div className="btn-group">
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Dependent"
                                onClick={() => {
                                  setEditingDependent(dep);
                                  setShowAddModal(true);
                                }}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                title="Remove Dependent"
                                onClick={() => handleDeleteDependent(dep.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Dependent Modal */}
      {showAddModal && (
        <DependentModal
          dependent={editingDependent}
          staffId={staffId}
          onClose={() => {
            setShowAddModal(false);
            setEditingDependent(null);
          }}
          onSuccess={() => {
            fetchDependents();
            setShowAddModal(false);
            setEditingDependent(null);
          }}
        />
      )}

      {/* CSS Styles - Matching your existing styles */}
      <style>{`
        .product-details-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .sidebar { width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; border-right: 1px solid #e5e7eb; }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .brand-logo { color: #333; font-size: 24px; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #F89423; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none; transition: all 0.3s ease; font-weight: 500; }
        .nav-link:hover, .nav-link.active { background-color: #F89423; color: white; border-left: 4px solid white; }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .content-wrapper { padding: 20px; }
        .page-header { margin-bottom: 24px; }
        .btn-back { background: none; border: none; font-size: 18px; color: #666; cursor: pointer; padding: 8px; border-radius: 50%; transition: background 0.2s; }
        .btn-back:hover { background: #f0f0f0; }
        .card { border-radius: 12px; }
        .badge.bg-pink { background-color: #e83e8c !important; }
        .modal.fade.show { display: block; }
        .modal-dialog-scrollable { max-height: calc(100vh - 3.5rem); }
        .modal-body { max-height: calc(100vh - 12rem); overflow-y: auto; }
        @media (max-width: 992px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.sidebar-open { transform: translateX(0); }
          .main-content { margin-left: 0; }
        }
      `}</style>
    </div>
  );
};

// Dependent Modal Component
const DependentModal = ({ dependent, staffId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: dependent?.firstName || '',
    lastName: dependent?.lastName || '',
    relationship: dependent?.relationship || '',
    age: dependent?.age || '',
    gender: dependent?.gender || '',
    phone: dependent?.phone || '',
    email: dependent?.email || '',
    dateOfBirth: dependent?.dateOfBirth || '',
    photo: dependent?.photo || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.relationship) newErrors.relationship = 'Relationship is required';
    if (formData.age && (formData.age < 0 || formData.age > 120)) newErrors.age = 'Age must be between 0 and 120';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const session = getUserSession();
      
      const payload = {
        StaffId: parseInt(staffId),
        CompanyId: session.companyId,
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        // Include Id for updates
        ...(dependent && { Id: dependent.id })
      };

      const endpoint = dependent 
        ? `${API_BASE}/Settings/update-dependent`
        : `${API_BASE}/Settings/save-dependent`;

      const response = await authenticatedFetch(
        endpoint,
        'POST',
        payload,
        { headers: getAuthHeaders() }
      );

      if (response?.success === true || response?.status === 'success') {
        toast.success(dependent ? 'Dependent updated successfully' : 'Dependent added successfully');
        onSuccess();
      } else {
        toast.error(response?.message || 'Failed to save dependent');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save dependent');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              {dependent ? '✏️ Edit Dependent' : '➕ Add Dependent'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">First Name <span className="text-danger">*</span></label>
                  <input 
                    type="text" 
                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                    value={formData.firstName}
                    onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                  {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Last Name <span className="text-danger">*</span></label>
                  <input 
                    type="text" 
                    className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                    value={formData.lastName}
                    onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                  {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Relationship <span className="text-danger">*</span></label>
                  <select 
                    className={`form-select ${errors.relationship ? 'is-invalid' : ''}`}
                    value={formData.relationship}
                    onChange={e => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                  >
                    <option value="">Select Relationship</option>
                    <option value="spouse">Spouse</option>
                    <option value="son">Son</option>
                    <option value="daughter">Daughter</option>
                    <option value="father">Father</option>
                    <option value="mother">Mother</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.relationship && <div className="invalid-feedback">{errors.relationship}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-semibold">Age</label>
                  <input 
                    type="number" 
                    className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                    value={formData.age}
                    onChange={e => setFormData(prev => ({ ...prev, age: e.target.value }))}
                    min="0"
                    max="120"
                  />
                  {errors.age && <div className="invalid-feedback">{errors.age}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-semibold">Gender</label>
                  <select 
                    className="form-select"
                    value={formData.gender}
                    onChange={e => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Phone</label>
                  <input 
                    type="tel" 
                    className="form-control"
                    value={formData.phone}
                    onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="08012345678"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Email</label>
                  <input 
                    type="email" 
                    className="form-control"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Date of Birth</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={formData.dateOfBirth}
                    onChange={e => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Photo</label>
                  <div className="d-flex align-items-center gap-2">
                    {formData.photo ? (
                      <img 
                        src={formData.photo} 
                        alt="Preview" 
                        className="rounded-circle"
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          backgroundColor: BRAND_ORANGE_LIGHT,
                          color: BRAND_ORANGE
                        }}
                      >
                        <i className="fas fa-user"></i>
                      </div>
                    )}
                    <div>
                      <input 
                        type="file" 
                        className="form-control form-control-sm"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                      <small className="text-muted">JPG/PNG, max 2MB</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-top">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                type="submit" 
                className="btn text-white"
                style={{ backgroundColor: BRAND_ORANGE }}
                disabled={submitting}
              >
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                ) : (
                  <><i className="fas fa-check me-2"></i>{dependent ? 'Update' : 'Add'} Dependent</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StaffDependentsView;